/**
 * Access Control Page (System)
 * Users, roles, and permissions
 */
import React, { useState } from 'react';
import { 
  Lock, 
  Users, 
  Shield,
  Plus,
  Search,
  Filter,
  MoreVertical,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const USERS = [
  { id: 1, name: 'Sarah Chen', email: 'sarah.chen@company.com', role: 'Admin', status: 'active', lastActive: '2025-01-15T10:30:00' },
  { id: 2, name: 'Michael Park', email: 'michael.park@company.com', role: 'Compliance Manager', status: 'active', lastActive: '2025-01-15T09:15:00' },
  { id: 3, name: 'Emma Wilson', email: 'emma.wilson@company.com', role: 'Data Analyst', status: 'active', lastActive: '2025-01-14T16:45:00' },
  { id: 4, name: 'James Lee', email: 'james.lee@company.com', role: 'Auditor', status: 'active', lastActive: '2025-01-15T08:00:00' },
  { id: 5, name: 'Alex Turner', email: 'alex.turner@company.com', role: 'Viewer', status: 'inactive', lastActive: '2024-12-20T14:30:00' },
];

const ROLES = [
  { name: 'Admin', users: 1, permissions: 'Full access' },
  { name: 'Compliance Manager', users: 2, permissions: 'Edit & approve' },
  { name: 'Data Analyst', users: 3, permissions: 'Edit data' },
  { name: 'Auditor', users: 1, permissions: 'View & export' },
  { name: 'Viewer', users: 2, permissions: 'View only' },
];

export default function AccessControlPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  const filteredUsers = USERS.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeCount = USERS.filter(u => u.status === 'active').length;

  return (
    <div className="space-y-6" data-testid="access-control-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Access Control</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Manage users, roles, and permissions
          </p>
        </div>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
          data-testid="invite-user-btn"
        >
          <Plus size={16} />
          <span className="text-sm font-medium">Invite User</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Users size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{USERS.length}</div>
              <div className="text-xs text-[#6B6B6B]">Total Users</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <CheckCircle2 size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">{activeCount}</div>
              <div className="text-xs text-[#6B6B6B]">Active</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Shield size={18} className="text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{ROLES.length}</div>
              <div className="text-xs text-[#6B6B6B]">Roles</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Lock size={18} className="text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">1</div>
              <div className="text-xs text-[#6B6B6B]">Admins</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#1F1F1F]">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'users' 
              ? 'border-emerald-500 text-emerald-400'
              : 'border-transparent text-[#6B6B6B] hover:text-white'
          }`}
        >
          Users
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'roles' 
              ? 'border-emerald-500 text-emerald-400'
              : 'border-transparent text-[#6B6B6B] hover:text-white'
          }`}
        >
          Roles
        </button>
      </div>

      {activeTab === 'users' && (
        <>
          {/* Search */}
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
              data-testid="users-search-input"
            />
          </div>

          {/* Users Table */}
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1F1F1F]">
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Role</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Last Active</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1F1F1F]">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-[#151515] transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-white">{user.name}</p>
                        <p className="text-xs text-[#6B6B6B]">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-medium bg-[#1F1F1F] text-[#A0A0A0] rounded">
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        user.status === 'active' 
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-[#2F2F2F] text-[#6B6B6B]'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#6B6B6B]">
                      {new Date(user.lastActive).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button className="p-2 text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors">
                        <MoreVertical size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'roles' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {ROLES.map((role, idx) => (
            <div key={idx} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-white">{role.name}</h3>
                <span className="px-2 py-1 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded">
                  {role.users} users
                </span>
              </div>
              <p className="text-sm text-[#6B6B6B] mb-4">{role.permissions}</p>
              <button className="text-xs text-emerald-400 hover:underline">Edit permissions</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
